import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SparklesIcon, SpeakerWaveIcon, SpeakerXMarkIcon, PlusIcon, LockClosedIcon, DocumentTextIcon, XMarkIcon, CalculatorIcon,
} from '@heroicons/react/24/outline';
import useAssistant from '../../components/assistant/useAssistant';
import { AssistantConversation, AssistantComposer, CapabilityList } from '../../components/assistant/AssistantChat';
import { isAdmin } from '../../utils/permissions';
import { ageText } from '../../components/patients/PatientSearch';

const PERSIST_KEY = 'omniprac.assistant.conversation';
const BILLING_KEY = 'omniprac.assistant.billing';
const QUOTING_KEY = 'omniprac.assistant.quoting';

const readBilling = () => {
  try { return JSON.parse(sessionStorage.getItem(BILLING_KEY)) || null; } catch { return null; }
};
const readQuoting = () => {
  try { return JSON.parse(sessionStorage.getItem(QUOTING_KEY)) || null; } catch { return null; }
};
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * Assistant — OmniPrac's own section for the AI assistant. It spans every
 * enabled module (Theatre schedule, billing), so it lives here rather than
 * inside any one page.
 *
 * Arriving from a booking's Bill button (location state `billing`), the booking
 * and patient are pinned above the conversation and "Bill case" starts at once;
 * the doctor only dictates the procedure and diagnoses.
 * From a booking's "Quote with assistant" (location state `quoting`) the same with
 * "Quote case": the planned procedure is quoted, or the doctor says what to quote.
 */
const AssistantPage = () => {
  const admin = isAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(() => location.state?.billing || readBilling());
  const [quoting, setQuoting] = useState(() => (location.state?.billing ? null : location.state?.quoting || readQuoting()));

  const assistant = useAssistant({
    persistKey: PERSIST_KEY,
    billingContext: billing ? { bid: billing.bid, patient_id: billing.patient_id || null, start: billing.start || null, end: billing.end || null } : null,
    quoteContext: quoting ? { bid: quoting.bid, patient_id: quoting.patient_id } : null,
  });
  const { status, ready, speak, toggleSpeak, turns, reset, busy, send } = assistant;

  useEffect(() => {
    try {
      if (billing) sessionStorage.setItem(BILLING_KEY, JSON.stringify(billing));
      else sessionStorage.removeItem(BILLING_KEY);
    } catch { /* storage blocked — the banner just won't survive a reload */ }
  }, [billing]);

  useEffect(() => {
    try {
      if (quoting) sessionStorage.setItem(QUOTING_KEY, JSON.stringify(quoting));
      else sessionStorage.removeItem(QUOTING_KEY);
    } catch { /* storage blocked */ }
  }, [quoting]);

  // Pressed Bill on a booking: start billing straight away (once per press).
  const started = useRef(null);
  useEffect(() => {
    const fresh = location.state?.billing;
    if (!fresh || !ready || started.current === fresh.key) return;
    started.current = fresh.key;
    setBilling(fresh);
    setQuoting(null);
    navigate(location.pathname, { replace: true, state: null });
    // billingContext reaches the hook on the next render, so send after it.
    setTimeout(() => send('Bill case'), 0);
  }, [location.state, location.pathname, ready, navigate, send]);

  // Pressed "Quote with assistant" on a booking: start the quote straight away.
  useEffect(() => {
    const fresh = location.state?.quoting;
    if (!fresh || !ready || started.current === fresh.key) return;
    started.current = fresh.key;
    setQuoting(fresh);
    setBilling(null);
    navigate(location.pathname, { replace: true, state: null });
    setTimeout(() => send('Quote case'), 0);
  }, [location.state, location.pathname, ready, navigate, send]);

  const newConversation = () => {
    reset();
    setBilling(null);
    setQuoting(null);
  };

  return (
    <div className="flex h-full min-h-0 gap-6 p-6">
      <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
        <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3.5">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-gradient text-white">
            <SparklesIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-brand-deep">Assistant</h1>
            <p className="truncate text-xs text-slate-500">
              {status === null ? 'Checking…' : ready ? `Using ${status.model}` : 'Not available'}
            </p>
          </div>
          <button
            onClick={toggleSpeak}
            title={speak ? 'Stop reading replies aloud' : 'Read replies to voice requests aloud'}
            className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-brand-navy"
          >
            {speak ? <SpeakerWaveIcon className="h-5 w-5" /> : <SpeakerXMarkIcon className="h-5 w-5" />}
          </button>
          <button
            onClick={newConversation}
            disabled={(!turns.length && !billing && !quoting) || busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:border-brand-navy hover:text-brand-navy disabled:opacity-40"
          >
            <PlusIcon className="h-4 w-4" /> New conversation
          </button>
        </header>

        {billing && <BillingBanner billing={billing} onChange={setBilling} onClear={() => setBilling(null)} />}
        {quoting && <QuoteBanner quoting={quoting} onClear={() => setQuoting(null)} />}

        <AssistantConversation assistant={assistant} admin={admin} className="flex-1" />
        <AssistantComposer assistant={assistant} placeholder={billing ? 'Dictate the procedure and diagnoses…' : quoting ? 'Say the procedure to quote and the expected time…' : undefined} />
      </section>

      <aside className="hidden w-80 shrink-0 flex-col gap-4 overflow-y-auto lg:flex">
        <div>
          <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">What I can help with</h2>
          <CapabilityList assistant={assistant} />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-500">
          <p className="flex items-center gap-1.5 font-semibold text-slate-700">
            <LockClosedIcon className="h-4 w-4" /> How it stays safe
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            <li>It can only run the approved actions listed above.</li>
            <li>Every change is shown to you first and only happens when you confirm.</li>
            <li>It follows the same permissions as the rest of OmniPrac.</li>
            <li>Only the patient’s age and sex go to the AI — never their name.</li>
            <li>Every request is recorded{admin ? ' — see Settings' : ''}.</li>
          </ul>
        </div>
      </aside>
    </div>
  );
};

// The booking and patient being billed, with the anaesthetic times.
const BillingBanner = ({ billing, onChange, onClear }) => {
  const set = (k) => (e) => onChange({ ...billing, [k]: e.target.value || null });
  const time = 'rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-teal-200 bg-teal-50 px-5 py-2.5">
      <DocumentTextIcon className="h-5 w-5 shrink-0 text-brand-teal" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-brand-deep">
          Billing {billing.patient_name || 'a case'}
          {billing.patient_dob && <span className="font-normal text-slate-500"> · {ageText(billing.patient_dob)}{billing.patient_sex ? ` ${billing.patient_sex}` : ''}</span>}
          {billing.payer_text && <span className="font-normal text-slate-500"> · {billing.payer_text}</span>}
        </div>
        <div className="truncate text-xs text-slate-600">
          {[billing.date && fmtDate(billing.date), billing.hospital, billing.theatre, billing.surgeon].filter(Boolean).join(' · ')}
          {billing.planned_procedure && <> · planned: <b>{billing.planned_procedure}</b></>}
        </div>
      </div>
      <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
        Anaesthetic time
        <input type="time" value={billing.start || ''} onChange={set('start')} className={time} />
        <span className="font-normal text-slate-400">to</span>
        <input type="time" value={billing.end || ''} onChange={set('end')} className={time} />
      </label>
      <button onClick={onClear} title="Stop billing this booking" className="rounded-md p-1.5 text-slate-400 hover:bg-white hover:text-brand-navy">
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

// The booking and patient a quote is being made for.
const QuoteBanner = ({ quoting, onClear }) => (
  <div className="flex items-center gap-4 border-b border-teal-200 bg-teal-50 px-5 py-2.5">
    <CalculatorIcon className="h-5 w-5 shrink-0 text-brand-teal" />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-bold text-brand-deep">
        Quote for {quoting.patient_name || 'a booked patient'}
        {quoting.patient_dob && <span className="font-normal text-slate-500"> · {ageText(quoting.patient_dob)}{quoting.patient_sex ? ` ${quoting.patient_sex}` : ''}</span>}
        {quoting.payer_text && <span className="font-normal text-slate-500"> · {quoting.payer_text}</span>}
      </div>
      <div className="truncate text-xs text-slate-600">
        {[quoting.date && fmtDate(quoting.date), quoting.hospital, quoting.surgeon].filter(Boolean).join(' · ')}
        {quoting.planned_procedure && <> · planned: <b>{quoting.planned_procedure}</b></>}
      </div>
    </div>
    <button onClick={onClear} title="Stop quoting this booking" className="rounded-md p-1.5 text-slate-400 hover:bg-white hover:text-brand-navy">
      <XMarkIcon className="h-4 w-4" />
    </button>
  </div>
);

export default AssistantPage;
